import { Member } from "@/types/member";
import {
  Organization,
  OrganizationMember,
  OrganizationMemberInvite,
  OrganizationMemberInviteDetails,
  OrganizationMemberInviteUpdateData,
  OrganizationMemberUpdateData,
} from "@/types/organization";
import { User } from "@/types/user";

export async function createOrganizationMember(
  userId: string,
  organizationId: string,
  inviterId: string,
  inviteToken: string,
  role: string,
  status: string,
): Promise<Member> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/member", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        organizationId,
        inviterId,
        inviteToken,
        role,
        status,
      }),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create organization user");
    }

    try {
      const { member } = await response.json();
      return member;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create organization");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function deleteOrganizationMember(
  memberId: string,
  userId: string,
  organizationId: string,
): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/member", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({ memberId, userId, organizationId }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete organization user");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getOrganizationMembers(
  organizationId: string,
  forceRefresh = false,
): Promise<Member[]> {
  const url = forceRefresh
    ? `/api/member?orgId=${organizationId}&_=${Date.now()}`
    : `/api/member?orgId=${organizationId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    cache: forceRefresh ? "no-store" : "force-cache",
    next: forceRefresh
      ? undefined
      : {
          tags: ["members"],
          revalidate: 3600,
        },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to get organization members");
  }

  try {
    const { members } = await response.json();
    return members;
  } catch (error: any) {
    throw new Error(error.message || "Failed to get organization members");
  }
}

export async function getOrganizationMember(
  userId: string,
  organizationId: string,
  forceRefresh = false,
): Promise<OrganizationMember> {
  const url = forceRefresh
    ? `/api/member?orgId=${organizationId}&userId=${userId}&_=${Date.now()}`
    : `/api/member?orgId=${organizationId}&userId=${userId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    cache: forceRefresh ? "no-store" : "force-cache",
    next: forceRefresh
      ? undefined
      : {
          tags: ["member"],
          revalidate: 3600,
        },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to get organization user");
  }

  try {
    const { organizationMember } = await response.json();
    return organizationMember;
  } catch (error: any) {
    throw new Error(error.message || "Failed to get organization user");
  }
}

export async function sendOrganizationInvites(
  organization: Organization,
  user: User,
  invites: OrganizationMemberInvite[],
): Promise<OrganizationMemberInvite[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/invite", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organization, user, invites }),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to send organization user invites",
      );
    }

    try {
      const { results } = await response.json();
      return results;
    } catch (error: any) {
      throw new Error(
        error.message || "Failed to send organization user invites",
      );
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function updateOrganizationMember(
  orgMemberData: OrganizationMemberUpdateData,
): Promise<Member> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/member", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgMemberData),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update organization user");
    }

    try {
      const { member } = await response.json();
      return member;
    } catch (error: any) {
      throw new Error(error.message || "Failed to update organization user");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

/* Organization member invitations */

export async function getOrganizationMemberInvitationDetails(
  orgId: string,
  userId: string,
  token: string,
): Promise<OrganizationMemberInviteDetails | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(
      `/api/member/public?orgId=${orgId}&userId=${userId}&token=${token}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-store",
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error ||
          "Failed to get organization member invitation details",
      );
    }

    try {
      const { memberInvitation } = await response.json();
      return memberInvitation;
    } catch (error: any) {
      throw new Error(
        error.message || "Failed to get organization member invitation details",
      );
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function updateOrganizationMemberByInivitationDetails(
  orgMemberInviteData: OrganizationMemberInviteUpdateData,
): Promise<{ userId: string | null; organizationId: string }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/member/public", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(orgMemberInviteData),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error ||
          `Failed to ${orgMemberInviteData.isDeclined ? "decline" : "accept"} organization member invite`,
      );
    }

    try {
      const { userId, organizationId } = await response.json();
      return { userId, organizationId };
    } catch (error: any) {
      throw new Error(
        error.message ||
          `Failed to ${orgMemberInviteData.isDeclined ? "decline" : "accept"} organization member invite`,
      );
    }
  } finally {
    clearTimeout(timeoutId);
  }
}
